import { useCallback, useRef } from "react";
import { useMutation } from "@apollo/client/react";

import type { MapPin } from "#app/components/map/map-types";
import { ReverseGeocodeAddressDocument } from "#app/lib/data";

export function useCreateMapPin() {
  const [reverseGeocode] = useMutation(ReverseGeocodeAddressDocument);
  const requestIdRef = useRef(0);

  const createPin = useCallback(
    async (latitude: number, longitude: number): Promise<MapPin | null> => {
      const currentRequestId = ++requestIdRef.current;

      try {
        const { data } = await reverseGeocode({
          variables: { lat: latitude, lng: longitude },
        });

        if (currentRequestId !== requestIdRef.current) return null;

        const address = data?.reverseGeocodeAddress;
        const lat = address?.latLng?.lat ?? latitude;
        const lng = address?.latLng?.lng ?? longitude;

        return {
          id: `new-${lat}-${lng}`,
          latitude: lat,
          longitude: lng,
          color: "grey",
          count: 0,
          addressId: address?.addressId,
          addressLine1: address?.addressLine1 ?? "",
          wholeAddress: address?.wholeAddress ?? "",
          city: address?.city ?? "",
          stateProvince: address?.stateProvince ?? "",
          postalCode: address?.postalCode ?? "",
          issueIds: [],
          propertyDetails: address?.propertyDetails ?? null,
          isLocal: true,
        };
      } catch (err) {
        console.error("Failed to create map pin:", err);
        return null;
      }
    },
    [reverseGeocode],
  );

  const cancel = useCallback(() => {
    requestIdRef.current++;
  }, []);

  return { createPin, cancel };
}
